import markSvg from '@/assets/mark.svg';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

export interface AppErrorScreenProps {
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  fullscreen?: boolean;
}

/**
 * Standalone error fallback for unrecoverable app crashes.
 *
 * Rendered by the root Sentry.ErrorBoundary in main.tsx, outside all
 * providers. Detects theme independently and avoids provider-dependent
 * components for maximum reliability.
 */
export default function ErrorFallback() {
  // Side effect only -- ensure the `dark` class is present on <html>
  // so Tailwind dark: variants resolve correctly while we render
  // outside the ThemeProvider.
  ensureDarkModeClass();

  return (
    <AppErrorScreen
      title="Something went wrong"
      description="The app hit an unexpected UI error. Reloading usually restores your session."
      actionLabel="Reload"
      onAction={() => window.location.reload()}
      fullscreen
    />
  );
}

/**
 * Minimal full-pane error screen.
 *
 * Compact product-system failure state. The quiet gray Antioch mark
 * anchors the page, while the copy sits in a narrow left-aligned
 * column so the state reads like app chrome rather than a centered
 * marketing poster.
 * Used both for unrecoverable app crashes (root Sentry boundary) and
 * for scoped failures like the workspace stream view crashing without
 * taking the whole app down.
 */
export function AppErrorScreen({
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  fullscreen = false,
}: AppErrorScreenProps) {
  return (
    <div
      className={cn(
        'relative flex w-full flex-col items-center justify-center overflow-hidden bg-white px-6 py-12 dark:bg-[#0a0f1a]',
        fullscreen ? 'fixed inset-0 min-h-screen' : 'min-h-full'
      )}
    >
      {/* Faint ambient backdrop -- gives the page just enough depth
          to feel intentional without reading as a heavy card. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_42%,rgba(15,23,42,0.045),transparent_58%)] dark:bg-[radial-gradient(ellipse_at_50%_42%,rgba(148,163,184,0.065),transparent_58%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-white/10"
      />

      <div className="relative flex w-full max-w-[360px] -translate-y-4 flex-col items-start text-left">
        <img src={markSvg} alt="" className="mb-5 w-[72px] opacity-[0.14] dark:opacity-[0.18] dark:invert" />

        <h1 className="font-semibold text-[19px] text-gray-950 leading-[1.2] tracking-tight dark:text-gray-100">
          {title}
        </h1>
        <p className="mt-2 max-w-[20rem] text-[13.5px] text-gray-500 leading-relaxed dark:text-gray-400">
          {description}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={onAction} className="px-3 shadow-sm">
            {actionLabel}
          </Button>
          {secondaryActionLabel && onSecondaryAction && (
            <Button variant="outline" size="sm" onClick={onSecondaryAction} className="px-3">
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ensure the `dark` class is applied to `<html>` when the user's
 * stored / system preference is dark, so Tailwind dark: variants
 * resolve correctly outside the ThemeProvider.
 */
function ensureDarkModeClass(): void {
  if (document.documentElement.classList.contains('dark')) return;

  try {
    const stored = localStorage.getItem('antioch-theme');
    if (stored === 'dark') {
      document.documentElement.classList.add('dark');
      return;
    }
    if (stored === 'light') return;
  } catch {
    // localStorage unavailable
  }

  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    document.documentElement.classList.add('dark');
  }
}
